import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { ArrowLeft, Key, CheckCircle, XCircle, Clock, Copy, Trash2, Shield } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import {
  getPendingLicenseRequests,
  getAllWhitelistedHardware,
  approveLicenseRequest,
  rejectLicenseRequest,
  deactivateHardware,
  updateHardwareNotes,
  LicenseRequest,
  WhitelistedHardware
} from '@/lib/licensing'
import { isSuperAdmin } from '@/lib/roles'
import { useTranslation } from 'react-i18next'

export const LicenseManagementPage: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { success, error } = useToast()
  const [pendingRequests, setPendingRequests] = useState<LicenseRequest[]>([])
  const [whitelistedHardware, setWhitelistedHardware] = useState<WhitelistedHardware[]>([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState<string | null>(null)
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null)
  const [notesDraft, setNotesDraft] = useState('')
  const [savingNotesId, setSavingNotesId] = useState<string | null>(null)

  // check if user is super admin
  useEffect(() => {
    if (!user) return
    
    if (!isSuperAdmin(user)) {
      error('Access Denied', 'You do not have permission to manage licenses')
      navigate('/dashboard')
    }
  }, [user, navigate, error])

  useEffect(() => {
    if (user && isSuperAdmin(user)) {
      loadData()
    }
  }, [user])

  const loadData = async () => {
    setLoading(true)
    try {
      const [requests, hardware] = await Promise.all([
        getPendingLicenseRequests(),
        getAllWhitelistedHardware()
      ])
      setPendingRequests(requests)
      setWhitelistedHardware(hardware)
    } catch (err) {
      console.error('Error loading license data:', err)
      error('Error', 'Failed to load license data')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (requestId: string) => {
    const notes = prompt('Add notes for this approval (optional):')
    setProcessing(requestId)
    try {
      const result = await approveLicenseRequest(requestId, notes || undefined)
      if (result.success) {
        success('Approved', result.message)
        await loadData()
      } else {
        error('Error', result.message)
      }
    } catch (err) {
      error('Error', 'Failed to approve license')
    } finally {
      setProcessing(null)
    }
  }

  const handleReject = async (requestId: string) => {
    const reason = prompt('Reason for rejection:')
    if (!reason) return

    setProcessing(requestId)
    try {
      const result = await rejectLicenseRequest(requestId, reason)
      if (result.success) {
        success('Rejected', result.message)
        await loadData()
      } else {
        error('Error', result.message)
      }
    } catch (err) {
      error('Error', 'Failed to reject license')
    } finally {
      setProcessing(null)
    }
  }

  const handleDeactivate = async (hardwareId: string) => {
    if (!confirm('Are you sure you want to deactivate this hardware license?')) return

    setProcessing(hardwareId)
    try {
      const result = await deactivateHardware(hardwareId)
      if (result.success) {
        success('Deactivated', result.message)
        await loadData()
      } else {
        error('Error', result.message)
      }
    } catch (err) {
      error('Error', 'Failed to deactivate hardware')
    } finally {
      setProcessing(null)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    success('Copied!', 'Hardware ID copied to clipboard')
  }

  const startEditNotes = (hw: WhitelistedHardware) => {
    setEditingNotesId(hw.id)
    setNotesDraft(hw.notes ?? '')
  }

  const cancelEditNotes = () => {
    setEditingNotesId(null)
    setNotesDraft('')
  }

  const saveNotes = async (hardwareId: string) => {
    const trimmed = notesDraft.trim()
    const existing = whitelistedHardware.find(h => h.id === hardwareId)
    if (existing && (existing.notes ?? '') === trimmed) {
      cancelEditNotes()
      return
    }
    setSavingNotesId(hardwareId)
    try {
      const result = await updateHardwareNotes(hardwareId, trimmed || null)
      if (result.success) {
        setWhitelistedHardware(prev => prev.map(h =>
          h.id === hardwareId ? { ...h, notes: trimmed || null } : h
        ))
        cancelEditNotes()
      } else {
        error('Error', result.message)
      }
    } finally {
      setSavingNotesId(null)
    }
  }

  // only render if super admin
  if (!isSuperAdmin(user)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">{t('adminPages.accessDenied')}</h1>
          <p className="text-slate-600 mb-4">{t('licenseAdmin.superRequired')}</p>
          <Link to="/dashboard">
            <Button>{t('common.backToDashboard')}</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      {/* header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-6 py-4 flex items-center gap-4">
          <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            </Button>
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Key className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              {t('dashboardPage.licenseManagement')}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Approve or reject hardware license requests
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl">
        {/* stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {pendingRequests.length}
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">{t('settingsPage.pending')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {whitelistedHardware.filter(h => h.is_active).length}
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">{t('settingsPage.activeLicenses')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-600 dark:text-slate-400">
                {whitelistedHardware.filter(h => !h.is_active).length}
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">{t('licenseAdmin.inactiveLicenses')}</div>
            </CardContent>
          </Card>
        </div>

        {/* pending requests */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Pending License Requests ({pendingRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">{t('common.loading')}</div>
            ) : pendingRequests.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                No pending requests
              </div>
            ) : (
              <div className="space-y-3">
                {pendingRequests.map((req) => (
                  <div key={req.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900 dark:text-white mb-1">
                          {req.user_email}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{t('licenseAdmin.machine')}:</span>
                            <span>{req.machine_name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{t('licenseAdmin.device')}:</span>
                            <span>{req.device_name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Hardware ID:</span>
                            <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-xs">
                              {req.hardware_id}
                            </code>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyToClipboard(req.hardware_id)}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">IP:</span>
                            <span>{req.request_ip || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{t('licenseAdmin.requested')}:</span>
                            <span>{new Date(req.requested_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprove(req.id)}
                          disabled={processing === req.id}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white font-bold"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleReject(req.id)}
                          disabled={processing === req.id}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 border-red-300"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* whitelisted hardware */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-green-600" />
              Whitelisted Hardware ({whitelistedHardware.filter(h => h.is_active).length} active)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">{t('common.loading')}</div>
            ) : whitelistedHardware.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                No whitelisted hardware
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-800">
                    <tr>
                      <th className="text-left p-3 font-medium">{t('licenseAdmin.user')}</th>
                      <th className="text-left p-3 font-medium">{t('licenseAdmin.machine')}</th>
                      <th className="text-left p-3 font-medium">Hardware ID</th>
                      <th className="text-left p-3 font-medium">{t('licenseAdmin.approved')}</th>
                      <th className="text-left p-3 font-medium">{t('licenseAdmin.lastSeen')}</th>
                      <th className="text-left p-3 font-medium">{t('fields.status')}</th>
                      <th className="text-left p-3 font-medium">{t('fields.notes')}</th>
                      <th className="text-right p-3 font-medium">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {whitelistedHardware.map((hw) => (
                      <tr key={hw.id} className="border-t border-slate-200 dark:border-slate-700">
                        <td className="p-3">{hw.user_email}</td>
                        <td className="p-3">{hw.machine_name || '-'}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-xs">
                              {hw.hardware_id.substring(0, 16)}...
                            </code>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyToClipboard(hw.hardware_id)}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                        <td className="p-3 text-xs">{new Date(hw.approved_at).toLocaleDateString()}</td>
                        <td className="p-3 text-xs">
                          {hw.last_seen_at ? new Date(hw.last_seen_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-3">
                          {hw.is_active ? (
                            <span className="text-green-600 dark:text-green-400 text-sm font-medium">{t('settingsPage.licensed')}</span>
                          ) : (
                            <span className="text-slate-400 text-sm">{t('settingsPage.notLicensed')}</span>
                          )}
                        </td>
                        <td className="p-3 max-w-[260px]">
                          {editingNotesId === hw.id ? (
                            <div className="flex flex-col gap-1">
                              <textarea
                                value={notesDraft}
                                onChange={e => setNotesDraft(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Escape') { e.preventDefault(); cancelEditNotes() }
                                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveNotes(hw.id) }
                                }}
                                placeholder={t('licenseAdmin.addNote')}
                                rows={2}
                                autoFocus
                                className="w-full text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white resize-y"
                              />
                              <div className="flex gap-1 justify-end">
                                <button
                                  onClick={cancelEditNotes}
                                  disabled={savingNotesId === hw.id}
                                  className="text-[10px] px-2 py-0.5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => saveNotes(hw.id)}
                                  disabled={savingNotesId === hw.id}
                                  className="text-[10px] px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {savingNotesId === hw.id ? 'Saving…' : 'Save'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEditNotes(hw)}
                              className="text-left w-full text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white whitespace-pre-wrap break-words rounded px-1 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                              title={t('licenseAdmin.clickEdit')}
                            >
                              {hw.notes
                                ? hw.notes
                                : <span className="italic text-slate-400">{t('licenseAdmin.clickAdd')}</span>}
                            </button>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {hw.is_active && (
                            <Button
                              onClick={() => handleDeactivate(hw.id)}
                              disabled={processing === hw.id}
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Deactivate
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

